import type { ErrorMessages } from '@repo/types/messages/errors';
import type { AxiosResponse } from 'axios';
import type { ExternalToast } from 'sonner';

import { ErrorMessageHandler } from '@repo/lib/errorMessageHandler';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';

export function toasterMessageHandler(messages: ErrorMessages, error: Error | null, toastProps?: ExternalToast, data?: AxiosResponse): void {
  const handler = new ErrorMessageHandler(messages);
  if (isAxiosError(error)) {
    const errorMessage = handler.processError(error);
    toast.error(errorMessage.title, {
      description: errorMessage.detail,
      ...toastProps,
    });
    return;
  }
  if (data?.status) {
    const successMessage = handler.processByStatusCode(data.status);
    if (!successMessage) {
      return;
    }
    toast.warning(successMessage.title, {
      description: successMessage.detail,
      ...toastProps,
    });
  }
}

export function toasterPromiseHandler<T>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: { title: string; description?: string } | ((data: T) => { title: string; description?: string });
    error: ErrorMessages;
  },
  toastProps?: ExternalToast
) {
  return toast.promise(promise, {
    loading: messages.loading,
    success: (data: T) => {
      if (messages.success instanceof Function) {
        const result = messages.success(data);
        return { message: result.title, description: result.description };
      }
      return {
        message: messages.success.title,
        description: messages.success.description,
      };
    },
    error: (error) => {
      const handler = new ErrorMessageHandler(messages.error);
      const { title, detail } = handler.processError(error);
      return {
        message: title,
        description: detail,
      };
    },
    ...toastProps,
  });
}
